import { AbilityBuilder, createMongoAbility, subject as caslSubject, type MongoAbility } from '@casl/ability';
import { PROJECT_ROLES, type ProjectRole, roleAtLeast } from './roles.js';
import type { SessionUser } from './types.js';

export type Action = 'read' | 'create' | 'update' | 'delete' | 'invoke' | 'publish' | 'manage';

export type SubjectName =
  | 'Project'
  | 'Endpoint'
  | 'Dashboard'
  | 'Datasource'
  | 'Token'
  | 'Member'
  | 'RunLog'
  | 'User'
  | 'Instance'
  | 'all';

export type AppAbility = MongoAbility<[Action, SubjectName | Record<string, unknown>]>;

/**
 * The permission matrix, declared as data so it can be audited and unit-tested
 * without instantiating an ability. Each entry is the minimum project role that
 * unlocks `action` on `subject`.
 */
export const PERMISSION_MATRIX: {
  subject: Exclude<SubjectName, 'all' | 'Instance' | 'User'>;
  action: Action;
  minRole: ProjectRole;
}[] = [
  // A viewer is a consumer, not an insider: dashboards, the catalogue of
  // available APIs and their documentation, and the ability to call them.
  // Deliberately NOT included: members (who else is on the project),
  // datasources (connection details), and run logs (which carry request and
  // response payloads in their traces).
  { subject: 'Project', action: 'read', minRole: 'viewer' },
  { subject: 'Endpoint', action: 'read', minRole: 'viewer' },
  { subject: 'Endpoint', action: 'invoke', minRole: 'viewer' },
  { subject: 'Dashboard', action: 'read', minRole: 'viewer' },

  // Editors see the machinery behind the APIs.
  { subject: 'Datasource', action: 'read', minRole: 'editor' },
  { subject: 'RunLog', action: 'read', minRole: 'editor' },
  { subject: 'Member', action: 'read', minRole: 'editor' },

  // Editors build things.
  { subject: 'Endpoint', action: 'create', minRole: 'editor' },
  { subject: 'Endpoint', action: 'update', minRole: 'editor' },
  { subject: 'Endpoint', action: 'delete', minRole: 'editor' },
  { subject: 'Endpoint', action: 'publish', minRole: 'editor' },
  { subject: 'Dashboard', action: 'create', minRole: 'editor' },
  { subject: 'Dashboard', action: 'update', minRole: 'editor' },
  { subject: 'Dashboard', action: 'delete', minRole: 'editor' },
  { subject: 'Datasource', action: 'create', minRole: 'editor' },
  { subject: 'Datasource', action: 'update', minRole: 'editor' },
  { subject: 'Datasource', action: 'delete', minRole: 'editor' },

  // Admins run the project.
  { subject: 'Project', action: 'update', minRole: 'admin' },
  { subject: 'Token', action: 'read', minRole: 'admin' },
  { subject: 'Token', action: 'create', minRole: 'admin' },
  { subject: 'Token', action: 'delete', minRole: 'admin' },
  { subject: 'Member', action: 'create', minRole: 'admin' },
  { subject: 'Member', action: 'update', minRole: 'admin' },
  { subject: 'Member', action: 'delete', minRole: 'admin' },

  // Owners can dissolve it.
  { subject: 'Project', action: 'delete', minRole: 'owner' },
];

/** Every (action, subject) pair unlocked for a given project role. */
export function capabilitiesFor(role: ProjectRole): { action: Action; subject: SubjectName }[] {
  return PERMISSION_MATRIX.filter((r) => roleAtLeast(role, r.minRole)).map((r) => ({
    action: r.action,
    subject: r.subject,
  }));
}

/** Plain, dependency-free check used on hot server paths. */
export function roleCan(role: ProjectRole | null | undefined, action: Action, subject: SubjectName): boolean {
  if (!role) return false;
  const rule = PERMISSION_MATRIX.find((r) => r.action === action && r.subject === subject);
  if (!rule) return false;
  return roleAtLeast(role, rule.minRole);
}

/**
 * Builds a CASL ability scoped to the user's memberships. Used by the web UI to
 * gate affordances and by the server as a second line of defence.
 */
export function defineAbilityFor(user: SessionUser | null | undefined): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (!user || user.disabled) return build();

  if (user.globalRole === 'superadmin') {
    can('manage', 'all');
    return build();
  }

  // Every authenticated user can read and update their own account.
  can('read', 'User', { id: user.id });
  can('update', 'User', { id: user.id });
  can('create', 'Project');

  for (const membership of user.memberships) {
    const projectId = membership.projectId;
    for (const rule of PERMISSION_MATRIX) {
      if (!roleAtLeast(membership.role, rule.minRole)) continue;
      // A Project record is identified by `id`; everything else carries `projectId`.
      const conditions: Record<string, unknown> =
        rule.subject === 'Project' ? { id: projectId } : { projectId };
      can(rule.action, rule.subject, conditions as never);
    }
  }

  return build();
}

/** Tags a plain record with its subject type so CASL conditions apply. */
export function asSubject<T extends Record<string, unknown>>(name: SubjectName, record: T) {
  return caslSubject(name as string, record as Record<PropertyKey, unknown>) as unknown as Record<string, unknown>;
}

export { PROJECT_ROLES, roleAtLeast };
export type { ProjectRole };
